/**
 * Per-record sync drawer для связей. Симметричен SyncRecordDrawer (поля),
 * но показывает per-link состояние и применяет bulk-of-one для связей.
 */
import { useEffect, useState } from 'react';
import { Drawer, Button, Table, Tag, Tooltip, Typography, Space, message } from 'antd';
import type { TableColumnsType } from 'antd';
import { fetchLinksDiff, bulkLinksSync, getJob } from './api.ts';
import { useJobs } from '../jobs/JobsContext.tsx';
import type {
  LinksPushDiffResponse,
  LinksPullDiffResponse,
  LinksPushDiffEntry,
  LinksPullDiffEntry,
  LinksOrphanEntry,
} from '@lens/shared';

const { Text } = Typography;

interface Props {
  open: boolean;
  spaceId: string;
  itemId: string;
  direction: 'push' | 'pull';
  includeLinkTypes: string[];
  onClose: () => void;
  onApplied: () => void;
}

export function LinksRecordDrawer({
  open,
  spaceId,
  itemId,
  direction,
  includeLinkTypes,
  onClose,
  onApplied,
}: Props) {
  const [diff, setDiff] = useState<LinksPushDiffResponse | LinksPullDiffResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const { registerJob } = useJobs();
  /** Push: selected linkIds. Pull: selected `${otherJiraKey}|${type}|${direction}`. */
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setErrorMsg(null);
    fetchLinksDiff(spaceId, itemId, direction, { includeLinkTypes })
      .then((res) => {
        if (cancelled) return;
        setDiff(res);
        // Default: все actionable выбраны
        const initial = new Set<string>();
        if (direction === 'push') {
          for (const l of (res as LinksPushDiffResponse).links) {
            if (l.canPush) initial.add(l.linkId);
          }
        } else {
          for (const l of (res as LinksPullDiffResponse).links) {
            if (l.canPull) initial.add(`${l.otherJiraIssueKey}|${l.linkType.toLowerCase()}|${l.direction}`);
          }
          // Осиротевшие связи по умолчанию НЕ выбраны: удаление более деструктивно,
          // чем создание — пользователь должен явно подтвердить.
        }
        setSelectedKeys(initial);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setErrorMsg(err instanceof Error ? err.message : 'Не удалось загрузить diff');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, spaceId, itemId, direction, includeLinkTypes]);

  const handleApply = async () => {
    if (selectedKeys.size === 0) {
      void message.warning('Ничего не выбрано');
      return;
    }
    setApplying(true);
    try {
      const selections: Record<string, unknown> = {};
      if (direction === 'push') {
        selections[itemId] = { linkIds: Array.from(selectedKeys) };
      } else {
        const entries: Array<{ otherJiraKey: string; linkType: string; direction: 'outward' | 'inward' }> = [];
        const orphanLinkIds: string[] = [];
        for (const k of selectedKeys) {
          if (k.startsWith('orphan:')) {
            orphanLinkIds.push(k.slice('orphan:'.length));
          } else {
            const [otherJiraKey, linkType, dir] = k.split('|');
            entries.push({ otherJiraKey: otherJiraKey!, linkType: linkType!, direction: dir as 'outward' | 'inward' });
          }
        }
        selections[itemId] = { entries, ...(orphanLinkIds.length > 0 ? { orphanLinkIds } : {}) };
      }
      const res = await bulkLinksSync(spaceId, {
        itemIds: [itemId],
        direction,
        ...(includeLinkTypes.length > 0 ? { includeLinkTypes } : {}),
        selections: selections as never,
      });
      registerJob(
        res.jobId,
        `${direction === 'push' ? 'Синхронизация связей в Jira' : 'Импорт связей из Jira'} · одна запись`,
      );
      // Wait for job
      for (let i = 0; i < 60; i++) {
        const j = await getJob(res.jobId);
        if (j.status === 'success' || j.status === 'error' || j.status === 'cancelled') {
          if (j.status === 'error') throw new Error(j.error ?? 'job failed');
          break;
        }
        await new Promise((r) => setTimeout(r, 1000));
      }
      void message.success(direction === 'push' ? 'Push связей выполнен' : 'Pull связей выполнен');
      onApplied();
    } catch (err: unknown) {
      void message.error(err instanceof Error ? err.message : 'Ошибка применения');
    } finally {
      setApplying(false);
    }
  };

  const isPush = direction === 'push';
  const links = diff?.links ?? [];
  const orphans: LinksOrphanEntry[] = !isPush ? ((diff as LinksPullDiffResponse | null)?.orphans ?? []) : [];
  const actionableCount = isPush
    ? (diff as LinksPushDiffResponse | null)?.toPush ?? 0
    : ((diff as LinksPullDiffResponse | null)?.toPull ?? 0) + orphans.length;

  const toggle = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const pushCols: TableColumnsType<LinksPushDiffEntry> = [
    {
      title: '',
      key: 'check',
      width: 32,
      render: (_, row) => (
        <input
          type="checkbox"
          disabled={!row.canPush}
          checked={selectedKeys.has(row.linkId)}
          onChange={() => toggle(row.linkId)}
        />
      ),
    },
    {
      title: 'Целевая запись',
      key: 'target',
      render: (_, row) => (
        <div>
          <div style={{ fontWeight: 500 }}>{row.targetTitle}</div>
          {row.targetJiraIssueKey ? (
            <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 11 }}>{row.targetJiraIssueKey}</Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 11 }}>— нет в Jira</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Тип связи',
      dataIndex: 'linkType',
      key: 'linkType',
      width: 120,
      render: (t: string) => <Tag>{t}</Tag>,
    },
    {
      title: 'Состояние',
      dataIndex: 'state',
      key: 'state',
      width: 220,
      render: (s: string, row) => {
        if (s === 'in_jira') return <Tag color="default">уже в Jira</Tag>;
        if (s === 'not_in_jira') return <Tag color="blue">будет создана в Jira</Tag>;
        return <Tooltip title={row.message}><Tag color="default">нельзя</Tag></Tooltip>;
      },
    },
  ];

  const pullCols: TableColumnsType<LinksPullDiffEntry> = [
    {
      title: '',
      key: 'check',
      width: 32,
      render: (_, row) => {
        const k = `${row.otherJiraIssueKey}|${row.linkType.toLowerCase()}|${row.direction}`;
        return (
          <input
            type="checkbox"
            disabled={!row.canPull}
            checked={selectedKeys.has(k)}
            onChange={() => toggle(k)}
          />
        );
      },
    },
    {
      title: 'Другой конец (в Jira)',
      key: 'other',
      render: (_, row) => (
        <div>
          <div style={{ fontWeight: 500 }}>{row.otherTitle ?? '(не зеркалирован)'}</div>
          <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 11 }}>{row.otherJiraIssueKey}</Text>
        </div>
      ),
    },
    {
      title: 'Тип связи',
      dataIndex: 'linkType',
      key: 'linkType',
      width: 120,
      render: (t: string) => <Tag>{t}</Tag>,
    },
    {
      title: 'Направление',
      dataIndex: 'direction',
      key: 'direction',
      width: 110,
      render: (d: string) => <Tag>{d === 'outward' ? 'Lens → other' : 'other → Lens'}</Tag>,
    },
    {
      title: 'Состояние',
      dataIndex: 'state',
      key: 'state',
      width: 220,
      render: (s: string, row) => {
        if (s === 'in_lens') return <Tag color="default">уже в Lens</Tag>;
        if (s === 'not_in_lens') return <Tag color="purple">будет создана в Lens</Tag>;
        return <Tooltip title={row.message}><Tag color="default">нельзя</Tag></Tooltip>;
      },
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={760}
      title={diff ? (
        <div>
          <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 400 }}>
            {isPush ? '⇨ Push связей' : '⇦ Pull связей'}
            {diff.jiraIssueKey && <span style={{ marginLeft: 8, fontFamily: 'monospace' }}>{diff.jiraIssueKey}</span>}
          </div>
          <div style={{ marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={diff.itemTitle}>
            {diff.itemTitle}
          </div>
        </div>
      ) : 'Загрузка…'}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Закрыть</Button>
          <Button
            type="primary"
            loading={applying}
            disabled={selectedKeys.size === 0 || !!errorMsg}
            onClick={() => void handleApply()}
            data-testid="links-drawer-apply"
          >
            Применить ({selectedKeys.size})
          </Button>
        </div>
      }
    >
      {loading && <div style={{ padding: 40, textAlign: 'center' }}>Загрузка…</div>}
      {errorMsg && <Text type="danger">{errorMsg}</Text>}
      {diff && !loading && !errorMsg && (
        <>
          <Space size="large" style={{ marginBottom: 12 }}>
            <Text>
              {isPush ? 'К push:' : 'К pull:'} <b>{actionableCount}</b>
            </Text>
            {isPush && (
              <>
                <Text type="secondary">Уже в Jira: <b>{(diff as LinksPushDiffResponse).inJira}</b></Text>
                <Text type="secondary">Нельзя: <b>{(diff as LinksPushDiffResponse).unsyncable}</b></Text>
              </>
            )}
            {!isPush && (
              <>
                <Text type="secondary">Уже в Lens: <b>{(diff as LinksPullDiffResponse).inLens}</b></Text>
                <Text type="secondary">Нельзя: <b>{(diff as LinksPullDiffResponse).unsyncable}</b></Text>
              </>
            )}
          </Space>
          {isPush ? (
            <Table<LinksPushDiffEntry>
              rowKey={(r) => `${r.linkId}`}
              dataSource={links as LinksPushDiffEntry[]}
              columns={pushCols}
              size="small"
              pagination={false}
            />
          ) : (
            <Table<LinksPullDiffEntry>
              rowKey={(r) => `${r.otherJiraIssueKey}|${r.linkType}`}
              dataSource={links as LinksPullDiffEntry[]}
              columns={pullCols}
              size="small"
              pagination={false}
            />
          )}
          {!isPush && orphans.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <div style={{ marginBottom: 6, fontWeight: 500 }}>
                Удалённые в Jira ({orphans.length})
              </div>
              <div style={{ marginBottom: 6, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                Эти связи есть в Lens, но в Jira их уже нет. Отметьте, чтобы удалить и в Lens.
              </div>
              <Table<LinksOrphanEntry>
                rowKey={(r) => `orphan:${r.linkId}`}
                dataSource={orphans}
                size="small"
                pagination={false}
                columns={[
                  {
                    title: '',
                    key: 'check',
                    width: 32,
                    render: (_: unknown, row) => {
                      const k = `orphan:${row.linkId}`;
                      return (
                        <input
                          type="checkbox"
                          checked={selectedKeys.has(k)}
                          onChange={() => toggle(k)}
                        />
                      );
                    },
                  },
                  {
                    title: 'Другой конец',
                    key: 'other',
                    render: (_: unknown, row) => (
                      <div>
                        <div style={{ fontWeight: 500 }}>{row.otherTitle}</div>
                        <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 11 }}>{row.otherJiraIssueKey}</Text>
                      </div>
                    ),
                  },
                  {
                    title: 'Тип связи',
                    dataIndex: 'linkType',
                    key: 'linkType',
                    width: 120,
                    render: (t: string) => <Tag>{t}</Tag>,
                  },
                  {
                    title: 'Направление',
                    dataIndex: 'direction',
                    key: 'direction',
                    width: 110,
                    render: (d: string) => <Tag>{d === 'outward' ? 'Lens → other' : 'other → Lens'}</Tag>,
                  },
                  {
                    title: 'Действие',
                    key: 'action',
                    width: 180,
                    render: () => <Tag color="red">удалить из Lens</Tag>,
                  },
                ]}
              />
            </div>
          )}
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--color-text-secondary)' }}>
            ℹ Снимите галочку, чтобы исключить связь из {isPush ? 'push' : 'pull'}'а.
          </div>
        </>
      )}
    </Drawer>
  );
}

